import React from 'react';
import StandardPageLayout from '../components/StandardPageLayout';
import { Card, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Shield, Heart, Sparkles, Lock, Users, Award } from 'lucide-react';
import { LANDING_HERO_VIDEO_URL } from '../constants/heroVideo';

const About = () => {
  return (
    <StandardPageLayout
      heroTitle=""
      heroDescription=""
      heroVideoUrl={LANDING_HERO_VIDEO_URL}
      heroVideoOverlay={0.4}
      
      section2HeaderContent={
        <div className="text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-medium text-black/95 mb-3 sm:mb-4">
            About GoogLyn AI
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-black/70 max-w-3xl mx-auto mb-8 sm:mb-10">
            Built on 15+ years inside the logistics industry — we exist to bring elegant intelligence to the operations that keep the world moving.
          </p>
          <h2 className="text-3xl md:text-4xl font-medium text-black/90 mb-3 sm:mb-4">
            Our Story
          </h2>
          <div className="max-w-3xl mx-auto">
            <p className="text-lg text-black/70 mb-3 leading-relaxed">
              GoogLyn AI was founded by someone who spent over 15 years working inside the logistics and freight industry — and watched it run on spreadsheets, phone calls, and manual processes the entire time. We built GoogLyn because we knew exactly what was broken, and exactly how to fix it.
            </p>
            <p className="text-lg text-black/70 mb-3 leading-relaxed">
              We&apos;re building intelligent solutions purpose-built for freight, trucking, and logistics operations — not adapted from generic software. Every tool we ship comes from real industry experience, not assumptions.
            </p>
            <p className="text-lg text-black/70 leading-relaxed">
              Our mission is to pioneer an era where elegance and intelligence are one — making intuitive, beautiful AI technology accessible to all.
            </p>
          </div>
        </div>
      }

      section2bSectionClassName="-mt-52 md:-mt-[343px] -mb-24 md:-mb-44"
      section2bContent={
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 max-w-5xl mx-auto">
          {[
            { icon: Users, label: 'Industry Verticals', value: '8' },
            { icon: Award, label: 'Core Functions', value: '5' },
            { icon: Sparkles, label: 'Happy Clients', value: '5+' },
            { icon: Heart, label: 'Customer Satisfaction', value: '98.56%' },
          ].map((stat, idx) => (
            <Card key={idx} className="bg-[#E8EDE7] border-[#D4DDD1]/50 text-center">
              <CardHeader>
                <stat.icon className="h-8 w-8 mx-auto text-[#E8A0B8] mb-2" />
                <CardTitle className="text-3xl font-medium text-black/90">{stat.value}</CardTitle>
                <CardDescription className="text-black/60">{stat.label}</CardDescription>
              </CardHeader>
            </Card>
          ))}
        </div>
      }
      
      section2cTitle="Our Values"
      section2cDescription="We hold ourselves to two core values — Integrity and Trust — because getting these right, without compromise, is what lets us meet every other expectation our clients and partners have of us."
      section2cContent={
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {[
            {
              title: 'Integrity',
              description: 'We do what we say, every time — no shortcuts, no exceptions. It\'s the foundation every other promise stands on.',
              icon: Shield,
            },
            {
              title: 'Trust',
              description: 'Earned through consistency, not claimed through marketing. When integrity holds, everything else we owe our clients follows.',
              icon: Lock,
            },
          ].map((item, idx) => (
            <Card key={idx} className="h-full bg-white border-[#D4DDD1]/40 hover:bg-[#F5F7F5] hover:border-[#D4DDD1] transition-all duration-300">
              <CardHeader>
                <div className="w-12 h-12 rounded-xl bg-[#E8A0B8]/10 flex items-center justify-center mb-4">
                  <item.icon className="h-6 w-6 text-[#E8A0B8]" />
                </div>
                <CardTitle className="text-xl font-medium text-black/90 mb-2">
                  {item.title}
                </CardTitle>
                <CardDescription className="text-black/70">
                  {item.description}
                </CardDescription>
              </CardHeader>
            </Card>
          ))}
        </div>
      }

      section3Title="Our Philosophy"
      section3BackgroundClass="bg-[#E3E8E0]"
      section3Description="Our Brand Philosophy guides everything we create."
      section3Content={
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            {
              title: 'Elegance by Design',
              description: 'Design is how elegance becomes intelligence. We craft experiences that feel natural and beautiful.',
              icon: Sparkles,
            },
            {
              title: 'Human Before Machine',
              description: 'People before code, trust at the center. AI without integrity is just automation.',
              icon: Heart,
            },
            {
              title: 'Effortless Innovation',
              description: 'AI that anticipates and adapts. Technology that feels like second nature.',
              icon: Award,
            },
            {
              title: 'Accessible Intelligence',
              description: 'Luxury made familiar, intelligence made personal. AI for everyone.',
              icon: Users,
            },
            {
              title: 'Crafted, Not Engineered',
              description: 'Elegance lives in the smallest details. We craft, we don\'t just engineer.',
              icon: Shield,
            },
            {
              title: 'Quiet Brilliance',
              description: 'Intelligence that listens and moves with you. No noise, just results.',
              icon: Lock,
            },
          ].map((item, idx) => (
            <Card key={idx} className="h-full bg-white border-[#D4DDD1]/40 hover:bg-[#F5F7F5] hover:border-[#D4DDD1] transition-all duration-300">
              <CardHeader>
                <div className="w-12 h-12 rounded-xl bg-[#E8A0B8]/10 flex items-center justify-center mb-4">
                  <item.icon className="h-6 w-6 text-[#E8A0B8]" />
                </div>
                <CardTitle className="text-xl font-medium text-black/90 mb-2">
                  {item.title}
                </CardTitle>
                <CardDescription className="text-black/70">
                  {item.description}
                </CardDescription>
              </CardHeader>
            </Card>
          ))}
        </div>
      }
      
      ctaTitle="Join us in building the future"
      ctaDescription="We're always looking for talented individuals who share our vision of elegant intelligence."
      ctaButtonText="View careers"
      ctaButtonHref="/careers"
    />
  );
};

export default About;
